import { defineStore } from 'pinia'
import { useAlertsStore } from '@/store/pinia/alerts'
import { useOrganizationStore } from '@/store/pinia/organization'
import { useConfigRepositoryStore } from '@/store/pinia/organization/configRepository'
import { useStackStore } from '@/store/pinia/organization/stack'
import { ObjWithPrivateProps } from '@/utils/classes'
import ebMapping from '@/utils/config/external-backends-mapping'
import { extractStartStopPipelines } from '@/utils/helpers'
import { getDelegatedDeletionAlertHtml, isDeletionDelegatedToPipeline } from '@/utils/helpers/envComponentDeletion'
import { makeClearErrorsAction, makeInFlightRequestDeduper, makeResetStoreAction, makeSetErrorsAction, makeStartFetchAction, makeStopFetchAction } from '@/utils/helpers/pinia'
import i18n from '@/utils/plugins/i18n'

const dedupeProjectPipelinesRequest = makeInFlightRequestDeduper()
const dedupeProjectEnvironmentsRequest = makeInFlightRequestDeduper()
const PIPELINES_PAGE_SIZE = 100
const PIPELINES_MAX_PAGES = 50

// Monotonically-increasing counter so that if multiple search requests are in-flight
// simultaneously, only the most recent result is written to searchedEnvironments.
let _searchSeq = 0

export const state = () => ({
  componentPlugins: [],
  detail: null,
  searchedEnvironments: null,
  environmentsConfig: {},
  errors: {
    componentPlugins: [],
    env: [],
    envComponent: [],
    environmentsConfig: [],
    envComponentConfig: [],
    externalBackend: [],
    favorite: [],
    forms: [],
    formsValidation: [],
    logEntries: [],
    logSources: [],
    pipelines: [],
    project: [],
  },
  externalBackend: null,
  externalBackends: [],
  fetchInProgress: {
    componentPlugins: false,
    environments: false,
    environmentsConfig: false,
    envComponentConfig: false,
    pipelines: false,
  },
  forms: {
    config: null,
  },
  validatedForms: null,
  logEntries: [],
  logSources: [],
  pipelines: [],
  selected: {
    env: null,
  },
  envComponentsConfig: {},
})

function adaptPluginWidgets (widgets) {
  return (_.isArray(widgets) ? widgets : [])
    .map((widget) => ({
      title: _.get(widget, 'placement.config.tab_name')
        || _.get(widget, 'placement.config.title')
        || widget.type
        || '',
      relations: (_.get(widget, 'widget.relations') || []).filter((relation) => relation?.selector),
    }))
    .filter((widget) => !_.isEmpty(widget.relations))
}

function adaptComponentPlugin (raw) {
  const version = raw.install?.version || raw.latest || {}
  return {
    id: raw.id,
    name: raw.name,
    icon: version.icon || null,
    description: version.description || '',
    widgets: adaptPluginWidgets(version.widgets),
    enabled: !!raw.relation?.enabled,
    relations: raw.relation?.relations || {},
  }
}

function parseEnvironmentsList (environments) {
  return environments.map((env) => {
    env.components = env.components?.filter((component) => component.is_configured)
    if (!_.isPlainObject(_.get(env, 'cloud_provider'))) return env

    const parsedEnv = new ObjWithPrivateProps(env, {
      _cloudProvider: {
        ...env.cloud_provider,
      },
    })
    parsedEnv.cloud_provider_canonical = env.cloud_provider.canonical
    parsedEnv.cloud_provider_name = env.cloud_provider.name
    delete parsedEnv.cloud_provider

    return parsedEnv
  })
}

function parseProjectEnvironments (theProject) {
  const project = _.cloneDeep(theProject)
  project.favorite ??= false
  project.environments = parseEnvironmentsList(project.environments || [])
  return project
}

export const useProjectStore = defineStore('project', {
  state,
  getters: {
    componentPluginById: (state) => (id) => state.componentPlugins.find((plugin) => String(plugin.id) === String(id)),
    configRepositoryBranch: (state) => state.configRepository?.detail?.branch,
    configRepositoryCanonical: (state) => state.detail?.config_repository_canonical,
    envForms: (state) => {
      const { pipelines } = state
      const stackConfig = state.stack?.config
      if (_.isEmpty(stackConfig)) return {}

      const nonStartStopPipelines = extractStartStopPipelines(pipelines)?.regular
      return nonStartStopPipelines.reduce((acc, { environment: { canonical }, use_case: useCase }) => ({
        ...acc,
        [canonical]: _.get(stackConfig[useCase], 'forms'),
      }), {})
    },
    envs: (state) => _.get(state.detail, 'environments') || [],
    envComponents: (state) => (envCanonical) => {
      const env = state.detail?.environments.find(({ canonical }) => canonical === envCanonical)
      return env?.components || []
    },
    searchedEnvComponents: (state) => (envCanonical) => {
      if (!state.searchedEnvironments) return []
      return state.searchedEnvironments.find((env) => env.canonical === envCanonical)?.components ?? []
    },
    envComponent: (state) => (envCanonical, componentCanonical) => {
      const env = state.detail?.environments.find(({ canonical }) => canonical === envCanonical)
      return env?.components?.find(({ canonical }) => canonical === componentCanonical)
    },
    envComponentPipeline: (state) => (envCanonical, componentCanonical) => {
      const pipeline = state.pipelines
        .find(({ environment, component }) => environment?.canonical === envCanonical && component?.canonical === componentCanonical)
      return pipeline
    },
    envHasIncompatibleStack: (state) => (envCanonical) => {
      const env = state.detail?.environments.find(({ canonical }) => canonical === envCanonical)
      return _.some(env?.components, ({ service_catalog: stack }) => Number(stack.version) < 4)
    },
    eventProvider: (state) => state.externalBackends.find(({ purpose }) => purpose === 'events'),
    hasEnvs: (state) => !_.isEmpty(state.envs),
    hasNoExternalBackendErrors: (state) => _.isEmpty(state.errors.externalBackend),
    hasProject: (state) => !_.isEmpty(state.detail),
    infraViewExternalBackends: (state) => state.externalBackends.filter(({ purpose }) => purpose === 'remote_tfstate'),
    isFavorite: (state) => !!state.detail?.favorite,
    logProvider: (state) => state.externalBackends.find(({ purpose }) => purpose === 'logs'),
    project: (state) => state.detail,
    projectCanonical: (state) => state.detail?.canonical ?? null,
    projectName: (state) => state.detail?.name ?? null,
    stackCanonical: (state) => state.detail?.service_catalog?.canonical,
    stackRef: (state) => state.detail?.service_catalog?.ref,
    envComponentConfig: (state) => (envCanonical, componentCanonical) => {
      return state.envComponentsConfig[envCanonical]?.[componentCanonical] || {}
    },
  },
  actions: {
    /**
     * Clear errors for a specific key.
     */
    CLEAR_PROJ_ERRORS: makeClearErrorsAction(state),

    /**
     * Reset the whole module state to its initial shape.
     */
    RESET_PROJ_STATE: makeResetStoreAction(state),

    /**
     * Start fetch operation.
     */
    START_FETCH: makeStartFetchAction(),

    /**
     * Stop fetch operation.
     */
    STOP_FETCH: makeStopFetchAction(),

    /**
     * Replace the current errors array.
     */
    SET_ERRORS: makeSetErrorsAction(state),

    /**
     * Set project data.
     * @param {Object} project
     */
    SET_PROJECT (project) {
      this.detail = parseProjectEnvironments(project)
    },

    /**
     * Set project environments.
     * @param {Array} environments
     */
    SET_PROJECT_ENVS (environments) {
      if (this.detail) {
        this.detail.environments = parseEnvironmentsList(environments)
      }
    },

    /**
     * Select environment.
     * @param {string} envCanonical
     */
    SELECT_ENV (envCanonical) {
      this.selected.env = envCanonical
    },

    /**
     * Set external backends.
     * @param {Array} externalBackends
     */
    SET_EXTERNAL_BACKENDS (externalBackends) {
      this.externalBackends = externalBackends
    },

    /**
     * Set external backend.
     * @param {Object} externalBackend
     */
    SET_EXTERNAL_BACKEND (externalBackend) {
      this.externalBackend = externalBackend
    },

    /**
     * Set project environment config.
     * @param {string} envCanonical
     * @param {Object} config
     */
    SET_PROJECT_ENVIRONMENT_CONFIG ({ envCanonical, config }) {
      this.environmentsConfig[envCanonical] = config
    },

    /**
     * Set project pipelines.
     * @param {Array} pipelines
     */
    SET_PROJECT_PIPELINES (pipelines) {
      this.pipelines = pipelines
    },

    /**
     * Set forms config.
     * @param {Object} config
     */
    SET_FORMS_CONFIG (config) {
      this.forms.config = config
    },

    /**
     * Set validated forms.
     * @param {Object} forms
     */
    SET_VALIDATED_FORMS (forms) {
      this.validatedForms = forms
    },

    /**
     * Set log entries.
     * @param {Array} logEntries
     */
    SET_LOG_ENTRIES (logEntries) {
      this.logEntries = logEntries
    },

    /**
     * Set log sources.
     * @param {Array} logSources
     */
    SET_LOG_SOURCES (logSources) {
      this.logSources = logSources
    },

    /**
     * Toggle favorite status.
     */
    TOGGLE_FAVORITE () {
      if (this.detail) {
        this.detail.favorite = !this.detail.favorite
      }
    },

    /**
     * Set project environment component config.
     * @param {string} envCanonical
     * @param {string} componentCanonical
     * @param {Object} config
     */
    SET_PROJECT_ENV_COMPONENT_CONFIG ({ envCanonical, componentCanonical, config }) {
      if (!this.envComponentsConfig[envCanonical]) {
        this.envComponentsConfig[envCanonical] = {}
      }
      this.envComponentsConfig[envCanonical][componentCanonical] = config
    },

    /**
     * Get project data.
     * @param {Object} params
     * @param {string} params.projectCanonical
     * @param {string} params.envCanonical
     * @param {boolean} params.fetchDeps
     * @returns {Promise<void>}
     */
    async GET_PROJECT ({ projectCanonical, envCanonical = this.selected.env, fetchDeps = true }) {
      const orgStore = useOrganizationStore()
      const alertStore = useAlertsStore()

      this.CLEAR_PROJ_ERRORS('project')

      const { data, errors } = (await this.$cycloid.ydAPI.getProject(orgStore.orgCanonical, projectCanonical)) || {}
      if (data) {
        this.SET_PROJECT(data)
        if (!_.isEmpty(data.environments)) {
          const env = data.environments.includes(envCanonical) ? envCanonical : data.environments[0].canonical
          this.SELECT_ENV(env)
        }
        if (fetchDeps) this.GET_PROJECT_DEPS()
      }
      if (errors) alertStore.SHOW_ALERT({ type: 'error', content: errors })
    },

    /**
     * Get project dependencies.
     * @returns {Promise<void>}
     */
    async GET_PROJECT_DEPS () {
      const stackStore = useStackStore()
      const configRepositoryStore = useConfigRepositoryStore()

      const configRepositoryCanonical = this.detail?.config_repository_canonical
      const canGetConfigRepository = this.$cycloid.permissions.canDisplay('GetConfigRepository', configRepositoryCanonical)
      const stackRef = this.detail?.service_catalog?.ref

      const promises = []

      if (configRepositoryCanonical && canGetConfigRepository) {
        promises.push(configRepositoryStore.GET_CONFIG_REPOSITORY({ configRepositoryCanonical }))
      }

      if (stackRef) {
        promises.push(stackStore.GET_STACK({ stackRef }))
        promises.push(stackStore.GET_STACK_CONFIG({ stackRef }))
      }

      promises.push(this.GET_PROJECT_ENVS())

      await Promise.all(promises)
    },

    /**
     * Get project environments.
     * @param {Object} [params]
     * @param {string} [params.search] When provided, results are stored in searchedEnvironments
     *   instead of detail.environments, and the API is called with component_search[rlike].
     * @returns {Promise<void>}
     */
    async GET_PROJECT_ENVS ({ search } = {}) {
      const orgStore = useOrganizationStore()
      const alertStore = useAlertsStore()
      const requestKey = `${orgStore.orgCanonical}:${orgStore.projectCanonical}:${search ?? ''}`
      const params = search ? { 'component_search[rlike]': search } : {}
      await dedupeProjectEnvironmentsRequest(requestKey, async () => {
        const seq = search ? ++_searchSeq : undefined
        this.START_FETCH('environments')
        const { data, errors } = (await this.$cycloid.ydAPI.getProjectEnvironments(orgStore.orgCanonical, orgStore.projectCanonical, params)) || {}
        if (data) {
          if (search) {
            if (seq === _searchSeq) this.searchedEnvironments = parseEnvironmentsList(data)
          } else {
            this.SET_PROJECT_ENVS(data)
            this.searchedEnvironments = null
          }
        }
        if (errors) {
          alertStore.SHOW_ALERT({ type: 'error', content: errors })
          if (search) this.searchedEnvironments = null
        }
        this.STOP_FETCH('environments')
      })
    },

    /**
     * Create project.
     * @param {Object} project
     * @returns {Promise<boolean>}
     */
    async CREATE_PROJECT (project) {
      const orgStore = useOrganizationStore()
      const alertStore = useAlertsStore()

      this.CLEAR_PROJ_ERRORS('project')
      const response = await this.$cycloid.ydAPI.createProject(orgStore.orgCanonical, { ...project })
      const { errors } = response || {}
      if (errors || !response) {
        if (errors) {
          this.SET_ERRORS({ key: 'project', errors })
          alertStore.SHOW_ALERT({ type: 'error', content: errors })
        }
        return false
      }
      const content = i18n.global.t('alerts.success.project.created', { projectName: project.name })
      alertStore.SHOW_ALERT({ type: 'success', content })
      return true
    },

    /**
     * Update project.
     * @param {Object} params
     * @param {boolean} params.refetchProject
     * @param {boolean} params.displaySuccessMessage
     * @param {Object} params.project
     * @param {string} params.successMessage
     * @param {string} params.refreshConfigForEnvCanonical
     * @returns {Promise<boolean>}
     */
    async UPDATE_PROJECT ({ refetchProject = true, displaySuccessMessage = true, project, successMessage, refreshConfigForEnvCanonical }) {
      const orgStore = useOrganizationStore()
      const alertStore = useAlertsStore()

      this.CLEAR_PROJ_ERRORS('project')
      if (project?.service_catalog?.ref) project.service_catalog_ref = project.service_catalog.ref

      const response = await this.$cycloid.ydAPI.updateProject(orgStore.orgCanonical, project)
      const { data, errors } = response || {}
      if (errors || !response) {
        if (errors) {
          this.SET_ERRORS({ key: 'project', errors })
          alertStore.SHOW_ALERT({ type: 'error', content: errors })
        }
        return false
      }
      const content = successMessage || i18n.global.t('alerts.success.project.updated', { projectName: project.name })
      if (displaySuccessMessage) alertStore.SHOW_ALERT({ type: 'success', content })
      if (refetchProject) await this.GET_PROJECT({ projectCanonical: data?.canonical ?? project.canonical })
      if (refreshConfigForEnvCanonical) await this.GET_PROJECT_ENVIRONMENT_CONFIG(refreshConfigForEnvCanonical)
      return true
    },

    /**
     * Delete project.
     * @param {Object} project
     * @param {Object} [options]
     * @param {boolean} [options.skipHooks] When true, sends `skip_hooks` so deletion runs after pipeline `on_delete` hooks (see API docs).
     * @returns {Promise<{ success: boolean, deletionDelegated: boolean, data?: object }>}
     */
    async DELETE_PROJECT (project, { skipHooks = false } = {}) {
      const orgStore = useOrganizationStore()
      const alertStore = useAlertsStore()

      this.CLEAR_PROJ_ERRORS('project')
      const deleteOpts = { ignore_config_files_err: true }
      if (skipHooks) deleteOpts.skip_hooks = true
      const { data, errors } = (await this.$cycloid.ydAPI.deleteProject(orgStore.orgCanonical, project.canonical, deleteOpts)) || {}
      const success = _.isEmpty(errors)
      const deletionDelegated = Boolean(success && !skipHooks && isDeletionDelegatedToPipeline(data))

      if (!success) {
        this.SET_ERRORS({ key: 'project', errors })
        return { success: false, deletionDelegated: false }
      }

      if (deletionDelegated) {
        alertStore.SHOW_ALERT({
          type: 'info',
          title: i18n.global.t('alerts.info.project.deletionDelegated.title'),
          content: getDelegatedDeletionAlertHtml(
            data,
            i18n.global.t.bind(i18n.global),
            {
              orgCanonical: orgStore.orgCanonical,
              projectCanonical: project.canonical,
            },
            { localePrefix: 'alerts.info.project.deletionDelegated' },
          ),
          keepOpen: true,
        })
        return { success: true, deletionDelegated: true, data }
      }

      const content = i18n.global.t('alerts.success.project.deleted', { projectName: project.name })
      alertStore.SHOW_ALERT({ type: 'success', content })
      return { success: true, deletionDelegated: false }
    },

    /**
     * Create environment.
     * @param {Object} params
     * @param {boolean} params.displaySuccessMessage
     * @param {Object} params.environment
     * @returns {Promise<boolean>}
     */
    async CREATE_ENV ({ displaySuccessMessage = true, environment }) {
      const orgStore = useOrganizationStore()
      const alertStore = useAlertsStore()

      this.CLEAR_PROJ_ERRORS('env')
      const response = await this.$cycloid.ydAPI.createProjectEnvironment(orgStore.orgCanonical, orgStore.projectCanonical, { ...environment })
      const { errors } = response || {}
      if (errors || !response) {
        if (errors) {
          alertStore.SHOW_ALERT({ type: 'error', content: errors })
          this.SET_ERRORS({ key: 'env', errors })
        }
        return false
      }
      const content = i18n.global.t('alerts.success.project.env.created', { envCanonical: environment.canonical })
      if (displaySuccessMessage) alertStore.SHOW_ALERT({ type: 'success', content })
      return true
    },

    /**
     * Link existing organization environment to project.
     * @param {Object} params
     * @param {string} params.envCanonical
     * @param {boolean} params.displaySuccessMessage
     * @returns {Promise<boolean>}
     */
    async LINK_ENVIRONMENT_TO_PROJECT ({ envCanonical, displaySuccessMessage = true }) {
      const orgStore = useOrganizationStore()
      const alertStore = useAlertsStore()

      this.CLEAR_PROJ_ERRORS('env')
      const response = await this.$cycloid.ydAPI.linkEnvironmentToProject(orgStore.orgCanonical, orgStore.projectCanonical, envCanonical)
      const { errors } = response || {}
      if (errors || !response) {
        if (errors) {
          alertStore.SHOW_ALERT({ type: 'error', content: errors })
          this.SET_ERRORS({ key: 'env', errors })
        }
        return false
      }
      const content = i18n.global.t('alerts.success.project.env.linked', { envCanonical })
      if (displaySuccessMessage) alertStore.SHOW_ALERT({ type: 'success', content })
      return true
    },

    /**
     * Update environment.
     * @param {Object} params
     * @param {boolean} params.displaySuccessMessage
     * @param {Object} params.environment
     * @param {boolean} params.refreshConfig
     * @param {boolean} params.refreshProject
     * @returns {Promise<boolean>}
     */
    async UPDATE_ENV ({ displaySuccessMessage = true, environment, refreshConfig, refreshProject = false }) {
      const orgStore = useOrganizationStore()
      const alertStore = useAlertsStore()

      this.CLEAR_PROJ_ERRORS('env')
      const response = await this.$cycloid.ydAPI.updateOrganizationEnvironment(orgStore.orgCanonical, {
        ...environment,
        type: environment.type || environment.environment_type?.canonical,
      })
      const { errors } = response || {}

      if (errors || !response) {
        if (errors) {
          alertStore.SHOW_ALERT({ type: 'error', content: errors })
          this.SET_ERRORS({ key: 'env', errors })
        }
        return false
      }

      const content = i18n.global.t('alerts.success.environment.updated')
      if (displaySuccessMessage) alertStore.SHOW_ALERT({ type: 'success', content })
      if (refreshProject) await this.GET_PROJECT({ projectCanonical: orgStore.projectCanonical })
      if (refreshConfig) await this.GET_PROJECT_ENVIRONMENT_CONFIG(environment.canonical)
      return true
    },

    /**
     * Delete environment.
     * @param {Object} params
     * @param {boolean} params.displaySuccessMessage
     * @param {string} params.envCanonical
     * @param {boolean} [params.skipHooks] When true, sends `skip_hooks` (after pipeline hooks complete).
     * @returns {Promise<{ success: boolean, deletionDelegated: boolean, data?: object }>}
     */
    async UNLINK_ENV ({ displaySuccessMessage = true, envCanonical, skipHooks = false }) {
      const orgStore = useOrganizationStore()
      const alertStore = useAlertsStore()

      this.CLEAR_PROJ_ERRORS('env')
      const deleteOpts = { ignore_config_files_err: true }
      if (skipHooks) deleteOpts.skip_hooks = true
      const { data, errors } = (await this.$cycloid.ydAPI.deleteProjectEnvironment(orgStore.orgCanonical, orgStore.projectCanonical, envCanonical, deleteOpts)) || {}
      const success = _.isEmpty(errors)
      const deletionDelegated = Boolean(success && !skipHooks && isDeletionDelegatedToPipeline(data))

      if (!success) {
        this.SET_ERRORS({ key: 'env', errors })
        return { success: false, deletionDelegated: false }
      }

      if (deletionDelegated) {
        if (displaySuccessMessage) {
          alertStore.SHOW_ALERT({
            type: 'info',
            title: i18n.global.t('alerts.info.environment.deletionDelegated.title'),
            content: getDelegatedDeletionAlertHtml(
              data,
              i18n.global.t.bind(i18n.global),
              {
                orgCanonical: orgStore.orgCanonical,
                projectCanonical: orgStore.projectCanonical,
                envCanonical,
              },
              { localePrefix: 'alerts.info.environment.deletionDelegated' },
            ),
            keepOpen: true,
          })
        }
        return { success: true, deletionDelegated: true, data }
      }

      const project = _.cloneDeep(this.detail)
      project.environments = _.filter(this.detail.environments, ({ canonical }) => canonical !== envCanonical)
      this.SET_PROJECT(project)
      const content = i18n.global.t('alerts.success.project.env.unlinked', { envCanonical })
      if (displaySuccessMessage) alertStore.SHOW_ALERT({ type: 'success', content })
      return { success: true, deletionDelegated: false }
    },

    /**
     * Create external backend.
     * @param {Object} externalBackend
     * @returns {Promise<boolean>}
     */
    async CREATE_EXTERNAL_BACKEND (externalBackend) {
      const orgStore = useOrganizationStore()

      this.CLEAR_PROJ_ERRORS('externalBackend')
      if (_.has(externalBackend, 'project_canonical')) externalBackend.project_canonical = orgStore.projectCanonical
      const { errors } = (await this.$cycloid.ydAPI.createExternalBackend(orgStore.orgCanonical, externalBackend)) || {}
      if (errors) this.SET_ERRORS({ key: 'externalBackend', errors })
      return _.isEmpty(errors)
    },

    /**
     * List per-component plugin settings (org-installed plugins with relations).
     * @param {Object} params
     * @param {string} params.projectCanonical
     * @param {string} params.envCanonical
     * @param {string} params.componentCanonical
     * @returns {Promise<boolean>}
     */
    async GET_COMPONENT_PLUGINS ({ projectCanonical, envCanonical, componentCanonical }) {
      const orgStore = useOrganizationStore()
      const alertStore = useAlertsStore()

      this.CLEAR_PROJ_ERRORS('componentPlugins')
      this.fetchInProgress.componentPlugins = true
      const { data, errors } = (await this.$cycloid.ydAPI.listComponentPlugins(
        orgStore.orgCanonical,
        projectCanonical,
        envCanonical,
        componentCanonical,
      )) || {}
      this.fetchInProgress.componentPlugins = false

      if (data) this.componentPlugins = data.map(adaptComponentPlugin)
      if (errors) alertStore.SHOW_ALERT({ type: 'error', content: errors })
      return _.isEmpty(errors)
    },

    /**
     * Toggle a plugin on/off for a component and/or update its relations.
     * @param {Object} params
     * @param {string} params.projectCanonical
     * @param {string} params.envCanonical
     * @param {string} params.componentCanonical
     * @param {string} params.pluginId
     * @param {boolean} params.enabled
     * @param {Object} params.relations
     * @returns {Promise<boolean>}
     */
    async UPDATE_COMPONENT_PLUGIN_RELATION ({ projectCanonical, envCanonical, componentCanonical, pluginId, enabled, relations }) {
      const orgStore = useOrganizationStore()
      const alertStore = useAlertsStore()

      this.CLEAR_PROJ_ERRORS('componentPlugins')
      const { errors } = (await this.$cycloid.ydAPI.updateComponentPluginRelation(
        orgStore.orgCanonical,
        projectCanonical,
        envCanonical,
        componentCanonical,
        pluginId,
        { enabled, relations },
      )) || {}

      if (_.isEmpty(errors)) {
        const idx = this.componentPlugins.findIndex((plugin) => plugin.id === pluginId)
        if (idx !== -1) this.componentPlugins[idx] = { ...this.componentPlugins[idx], enabled, relations }
      } else {
        alertStore.SHOW_ALERT({ type: 'error', content: errors })
      }
      return _.isEmpty(errors)
    },

    /**
     * Get external backends.
     * @returns {Promise<boolean>}
     */
    async GET_EXTERNAL_BACKENDS () {
      const orgStore = useOrganizationStore()
      const alertStore = useAlertsStore()

      this.CLEAR_PROJ_ERRORS('externalBackend')
      const { data, errors } = (await this.$cycloid.ydAPI.getExternalBackends(orgStore.orgCanonical)) || {}
      if (data) {
        const processedData = _(data)
          .filter((eb) => eb.project_canonical === orgStore.projectCanonical || eb.purpose === 'events')
          .map(ebMapping.remap)
          .value()
        this.SET_EXTERNAL_BACKENDS(processedData)
      }
      if (errors) alertStore.SHOW_ALERT({ type: 'error', content: errors })
      return _.isEmpty(errors)
    },

    /**
     * Get external backend.
     * @param {Object} params
     * @param {string} params.externalBackendId
     * @returns {Promise<boolean>}
     */
    async GET_EXTERNAL_BACKEND ({ externalBackendId }) {
      const orgStore = useOrganizationStore()
      const alertStore = useAlertsStore()

      const { data, errors } = (await this.$cycloid.ydAPI.getExternalBackend(orgStore.orgCanonical, externalBackendId)) || {}
      if (data) this.SET_EXTERNAL_BACKEND(ebMapping.remap(data))
      if (errors) alertStore.SHOW_ALERT({ type: 'error', content: errors })
      return _.isEmpty(errors)
    },

    /**
     * Update external backend.
     * @param {Object} params
     * @param {string} params.externalBackendId
     * @param {Object} params.config
     * @returns {Promise<boolean>}
     */
    async UPDATE_EXTERNAL_BACKEND ({ externalBackendId, config }) {
      const orgStore = useOrganizationStore()
      const alertStore = useAlertsStore()

      this.CLEAR_PROJ_ERRORS('externalBackend')
      const { data, errors } = (await this.$cycloid.ydAPI.updateExternalBackend(orgStore.orgCanonical, externalBackendId, config)) || {}
      if (data) this.SET_EXTERNAL_BACKEND(data)
      if (errors) {
        alertStore.SHOW_ALERT({ type: 'error', content: errors })
        this.SET_ERRORS({ key: 'externalBackend', errors })
      }
      return _.isEmpty(errors)
    },

    /**
     * Delete external backend.
     * @param {Object} params
     * @param {string} params.externalBackendId
     * @returns {Promise<boolean>}
     */
    async DELETE_EXTERNAL_BACKEND ({ externalBackendId }) {
      const orgStore = useOrganizationStore()
      const alertStore = useAlertsStore()

      const { errors } = (await this.$cycloid.ydAPI.deleteExternalBackend(orgStore.orgCanonical, externalBackendId)) || {}
      if (errors) alertStore.SHOW_ALERT({ type: 'error', content: errors })
      return _.isEmpty(errors)
    },

    /**
     * Get project environment config.
     * @param {string} envCanonical
     * @returns {Promise<void>}
     */
    async GET_PROJECT_ENVIRONMENT_CONFIG (envCanonical) {
      const orgStore = useOrganizationStore()

      this.CLEAR_PROJ_ERRORS('environmentsConfig')
      this.CLEAR_PROJ_ERRORS('forms')
      this.START_FETCH('environmentsConfig')
      const { data, errors } = (await this.$cycloid.ydAPI.getProjectEnvironmentConfig(orgStore.orgCanonical, orgStore.projectCanonical, envCanonical)) || {}
      if (data) this.SET_PROJECT_ENVIRONMENT_CONFIG({ envCanonical, config: data })
      if (errors) this.SET_ERRORS({ key: 'environmentsConfig', errors })
      this.STOP_FETCH('environmentsConfig')
    },

    /**
     * Get project pipelines.
     * @param {string} [projectCanonical] Defaults to the loaded project's canonical.
     *   Pass the route param explicitly when calling before `detail` has loaded
     *   (direct entry to overview pages) — otherwise it resolves to `null` and the
     *   request 404s, leaving status chips hidden.
     * @returns {Promise<void>}
     */
    async GET_PROJECT_PIPELINES (projectCanonical) {
      const orgStore = useOrganizationStore()
      const canonical = projectCanonical ?? orgStore.projectCanonical
      const requestKey = `${orgStore.orgCanonical}:${canonical}`
      await dedupeProjectPipelinesRequest(requestKey, async () => {
        this.CLEAR_PROJ_ERRORS('pipelines')
        this.START_FETCH('pipelines')

        // Page 1 reveals pagination.total so subsequent pages can be fetched in parallel.
        const first = await this.$cycloid.ydAPI.getProjectPipelines(
          orgStore.orgCanonical,
          canonical,
          { page_index: 1, page_size: PIPELINES_PAGE_SIZE },
        )
        const { data: firstData, errors: firstErrors, pagination: firstPagination } = first || {}

        if (firstErrors) {
          this.SET_ERRORS({ key: 'pipelines', errors: firstErrors })
          this.STOP_FETCH('pipelines')
          return
        }
        if (!Array.isArray(firstData)) { this.STOP_FETCH('pipelines'); return }

        const total = firstPagination?.total ?? firstData.length
        const lastPage = Math.min(Math.ceil(total / PIPELINES_PAGE_SIZE), PIPELINES_MAX_PAGES)

        // PIPELINES_MAX_PAGES is a runaway guard; warn if it silently truncates results.
        if (lastPage === PIPELINES_MAX_PAGES && total > PIPELINES_MAX_PAGES * PIPELINES_PAGE_SIZE) {
          console.warn(`[GET_PROJECT_PIPELINES] result capped at ${PIPELINES_MAX_PAGES} pages; ${total} pipelines reported`)
        }

        const restResponses = await Promise.all(
          _.range(2, lastPage + 1).map((pageIndex) =>
            this.$cycloid.ydAPI.getProjectPipelines(orgStore.orgCanonical, canonical, {
              page_index: pageIndex,
              page_size: PIPELINES_PAGE_SIZE,
            }),
          ),
        )

        const pageError = restResponses.find((r) => r?.errors)?.errors
        if (pageError) {
          this.SET_ERRORS({ key: 'pipelines', errors: pageError })
          this.STOP_FETCH('pipelines')
          return
        }

        this.SET_PROJECT_PIPELINES(_.sortBy(
          [...firstData, ...restResponses.flatMap((r) => r?.data ?? [])],
          ['name'],
        ))
        this.STOP_FETCH('pipelines')
      })
    },

    /**
     * Create forms config.
     * @param {Object} formsConfig
     * @returns {Promise<void>}
     */
    async CREATE_FORMS_CONFIG (formsConfig) {
      const orgStore = useOrganizationStore()

      const { data, errors } = (await this.$cycloid.ydAPI.createFormsConfig(orgStore.orgCanonical, orgStore.projectCanonical, formsConfig)) || {}
      if (data) this.SET_FORMS_CONFIG(data)
      if (errors) this.SET_ERRORS({ key: 'forms', errors })
    },

    /**
     * Validate forms config.
     * @param {Object} formsFile
     * @returns {Promise<void>}
     */
    async VALIDATE_FORMS_CONFIG (formsFile) {
      const orgStore = useOrganizationStore()

      this.CLEAR_PROJ_ERRORS('formsValidation')
      const { data, errors } = (await this.$cycloid.ydAPI.validateFormsFile(orgStore.orgCanonical, formsFile)) || {}
      if (errors) {
        // If processing errors were found, hide the previous forms
        this.SET_ERRORS({ key: 'formsValidation', errors })
        this.SET_VALIDATED_FORMS(null)
      } else if (data) {
        const { forms, errors: formErrors } = data
        this.SET_VALIDATED_FORMS(forms)

        // Feedback errors are rendered together with the forms
        if (!_.isEmpty(formErrors)) this.SET_ERRORS({ key: 'formsValidation', errors: formErrors })
      }
    },

    /**
     * Reset form validation state.
     * @returns {Promise<void>}
     */
    async RESET_FORM_VALIDATION_STATE () {
      this.SET_VALIDATED_FORMS(null)
      this.CLEAR_PROJ_ERRORS('formsValidation')
    },

    /**
     * Get log entries.
     * @param {Object} params
     * @param {string} params.envCanonical
     * @param {string} params.source
     * @param {string} params.begin
     * @param {string} params.end
     * @param {string} params.query
     * @returns {Promise<void>}
     */
    async GET_LOG_ENTRIES ({ envCanonical, source, begin, end, query }) {
      const orgStore = useOrganizationStore()

      this.CLEAR_PROJ_ERRORS('logEntries')
      const { data, errors } = (await this.$cycloid.ydAPI.getProjEnvLogEntries({ orgCanonical: orgStore.orgCanonical, projectCanonical: orgStore.projectCanonical, envCanonical, source, begin, end, query })) || {}
      if (data) this.SET_LOG_ENTRIES(data)
      if (errors) this.SET_ERRORS({ key: 'logEntries', errors })
    },

    /**
     * Get log sources.
     * @param {string} envCanonical
     * @returns {Promise<void>}
     */
    async GET_LOG_SOURCES (envCanonical) {
      const orgStore = useOrganizationStore()
      const alertStore = useAlertsStore()

      this.CLEAR_PROJ_ERRORS('logSources')
      const { data, errors } = (await this.$cycloid.ydAPI.getProjEnvLogSources(orgStore.orgCanonical, orgStore.projectCanonical, envCanonical)) || {}
      if (data) this.SET_LOG_SOURCES(data)
      else this.SET_LOG_SOURCES([])
      if (errors && errors[0].code !== 'NotFound') {
        alertStore.SHOW_ALERT({ type: 'error', content: errors })
      }
    },

    /**
     * Set favorite project.
     * @param {Object} project
     * @returns {Promise<void>}
     */
    async SET_FAVORITE_PROJECT (project) {
      const orgStore = useOrganizationStore()
      const alertStore = useAlertsStore()

      this.CLEAR_PROJ_ERRORS('favorite')
      const { errors } = (await this.$cycloid.ydAPI.createProjectFavorite(orgStore.orgCanonical, project.canonical)) || {}
      if (errors) return this.SET_ERRORS({ key: 'favorite', errors })

      if (this.detail) this.TOGGLE_FAVORITE()
      const content = i18n.global.t('alerts.success.project.favorite.created', { projectName: project.name })
      alertStore.SHOW_ALERT({ type: 'success', content })
    },

    /**
     * Unset favorite project.
     * @param {Object} project
     * @returns {Promise<void>}
     */
    async UNSET_FAVORITE_PROJECT (project) {
      const orgStore = useOrganizationStore()
      const alertStore = useAlertsStore()

      const { errors } = (await this.$cycloid.ydAPI.deleteProjectFavorite(orgStore.orgCanonical, project.canonical)) || {}
      if (errors) return this.SET_ERRORS({ key: 'favorite', errors })

      if (this.detail) this.TOGGLE_FAVORITE()
      const content = i18n.global.t('alerts.success.project.favorite.deleted', { projectName: project.name })
      alertStore.SHOW_ALERT({ type: 'success', content })
    },

    /**
     * Create environment component.
     * @param {Object} params
     * @param {string} params.envCanonical
     * @param {Object} params.component
     * @returns {Promise<boolean>}
     */
    async CREATE_ENV_COMPONENT ({ envCanonical, component }) {
      const orgStore = useOrganizationStore()
      const alertStore = useAlertsStore()

      this.CLEAR_PROJ_ERRORS('envComponent')
      const { errors } = (await this.$cycloid.ydAPI.createEnvComponent(orgStore.orgCanonical, orgStore.projectCanonical, envCanonical, component)) || {}
      if (errors) alertStore.SHOW_ALERT({ type: 'error', content: errors })
      return _.isEmpty(errors)
    },

    /**
     * Configure environment component.
     * @param {Object} params
     * @param {string} params.envCanonical
     * @param {string} params.componentCanonical
     * @param {Object} params.config
     * @param {boolean} params.displaySuccessMessage
     * @returns {Promise<boolean>}
     */
    async CONFIGURE_ENV_COMPONENT ({ displaySuccessMessage = true, envCanonical, componentCanonical, config }) {
      const orgStore = useOrganizationStore()
      const alertStore = useAlertsStore()

      this.CLEAR_PROJ_ERRORS('envComponent')
      const { errors } = await this.$cycloid.ydAPI.configureEnvComponent(orgStore.orgCanonical, orgStore.projectCanonical, envCanonical, componentCanonical, config) || {}
      if (_.isEmpty(errors) && displaySuccessMessage) {
        const content = i18n.global.t('alerts.success.envComponent.configured')
        alertStore.SHOW_ALERT({ type: 'success', content })
      }
      if (errors) alertStore.SHOW_ALERT({ type: 'error', content: errors })
      return _.isEmpty(errors)
    },

    /**
     * Delete environment component.
     * @param {Object} params
     * @param {boolean} params.displaySuccessMessage
     * @param {boolean} [params.displayErrorMessage] Set to false when the deletion is a rollback of
     * another failed call, so that the alert keeps naming what actually failed rather than the cleanup.
     * @param {string} params.envCanonical
     * @param {string} params.componentCanonical
     * @param {boolean} [params.skipHooks] When true, sends `skip_hooks` (second DELETE after `on_delete` hook; see stack pipeline hooks docs).
     * @param {boolean} [params.ignoreConfigFilesErr] When true, sends `ignore_config_files_err` (only when config/Git cleanup would otherwise block deletion).
     * @returns {Promise<{ success: boolean, deletionDelegated: boolean, data?: object }>}
     */
    async DELETE_ENV_COMPONENT ({
      displaySuccessMessage = true,
      displayErrorMessage = true,
      envCanonical,
      componentCanonical,
      skipHooks = false,
      ignoreConfigFilesErr = false,
    }) {
      const alertStore = useAlertsStore()
      const orgStore = useOrganizationStore()

      this.CLEAR_PROJ_ERRORS('envComponent')
      const deleteOpts = {}
      if (ignoreConfigFilesErr) deleteOpts.ignore_config_files_err = true
      if (skipHooks) deleteOpts.skip_hooks = true
      const { data, errors } = (await this.$cycloid.ydAPI.deleteEnvComponent(
        orgStore.orgCanonical,
        orgStore.projectCanonical,
        envCanonical,
        componentCanonical,
        deleteOpts,
      )) || {}
      const success = _.isEmpty(errors)
      const deletionDelegated = Boolean(success
        && !skipHooks
        && isDeletionDelegatedToPipeline(data))

      if (success && displaySuccessMessage) {
        if (deletionDelegated) {
          const delegationRouteParams = {
            orgCanonical: orgStore.orgCanonical,
            projectCanonical: orgStore.projectCanonical,
            envCanonical,
            componentCanonical,
          }
          alertStore.SHOW_ALERT({
            type: 'info',
            title: i18n.global.t('alerts.info.envComponent.deletionDelegated.title'),
            content: getDelegatedDeletionAlertHtml(data, i18n.global.t.bind(i18n.global), delegationRouteParams),
            keepOpen: true,
          })
        } else {
          const content = i18n.global.t('alerts.success.envComponent.deleted')
          alertStore.SHOW_ALERT({ type: 'success', content })
        }
      }
      if (errors && displayErrorMessage) alertStore.SHOW_ALERT({ type: 'error', content: errors })
      return { success, deletionDelegated, ...(data !== undefined ? { data } : {}) }
    },

    /**
     * Update environment component.
     * @param {Object} params
     * @param {string} params.envCanonical
     * @param {Object} params.component
     * @param {boolean} params.displaySuccessMessage
     * @returns {Promise<boolean>}
     */
    async UPDATE_ENV_COMPONENT ({ displaySuccessMessage = true, envCanonical, component }) {
      const orgStore = useOrganizationStore()
      const alertStore = useAlertsStore()

      this.CLEAR_PROJ_ERRORS('envComponent')
      const response = await this.$cycloid.ydAPI.updateEnvComponent(orgStore.orgCanonical, orgStore.projectCanonical, envCanonical, component)
      const { errors } = response || {}
      if (errors || !response) {
        if (errors) alertStore.SHOW_ALERT({ type: 'error', content: errors })
        return false
      }
      const content = i18n.global.t('alerts.success.envComponent.updated', { envComponentName: component.name })
      if (displaySuccessMessage) alertStore.SHOW_ALERT({ type: 'success', content })
      return true
    },

    /**
     * Get environment component config.
     * @param {Object} params
     * @param {string} params.envCanonical
     * @param {string} params.componentCanonical
     * @returns {Promise<void>}
     */
    async GET_ENV_COMPONENT_CONFIG ({ envCanonical, componentCanonical, queryParams = {} }) {
      const orgStore = useOrganizationStore()
      const alertStore = useAlertsStore()

      this.CLEAR_PROJ_ERRORS('envComponentConfig')
      this.START_FETCH('envComponentConfig')
      const { data, errors } = (await this.$cycloid.ydAPI.getComponentConfig(orgStore.orgCanonical, orgStore.projectCanonical, envCanonical, componentCanonical, queryParams)) || {}
      if (data) this.SET_PROJECT_ENV_COMPONENT_CONFIG({ envCanonical, componentCanonical, config: data })
      if (errors) alertStore.SHOW_ALERT({ type: 'error', content: errors })
      this.STOP_FETCH('envComponentConfig')
      return _.isEmpty(errors)
    },

  },
})
